import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPlus, faUserGraduate, faTriangleExclamation, faBan, faGlobe } from '@fortawesome/free-solid-svg-icons';
import './StudentPaidAvailability.css';

const API = process.env.REACT_APP_API_BASE_URL;

/* Same slots + same labels as before (Lebanon time) */
const SLOT_LABEL = {
  8: '8:00 AM',
  9: '9:00 AM',
  10: '10:00 AM',
  11: '11:00 AM',
  12: '12:00 PM',
  13: '1:00 PM',
  14: '2:30 PM',
  15: '3:30 PM',
  16: '4:30 PM',
  17: '5:30 PM',
  18: '6:30 PM',
  19: '7:30 PM',
  20: '8:30 PM',
  21: '9:30 PM',
  22: '10:30 PM',
};
const SLOTS = Object.keys(SLOT_LABEL).map(Number);
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const todayIndex = () => ((new Date().getDay() + 6) % 7) + 1; // 1 = Monday
const keyOf = (d, s) => `${d}-${s}`;

/* Same props as before: { assignedTeacher, studentId } */
const StudentPaidAvailability = ({ assignedTeacher, studentId }) => {
  const [teacherOpen, setTeacherOpen] = useState({}); // "d-s": true when the teacher is available
  const [allTaken, setAllTaken] = useState({}); // "d-s": true when any student has it
  const [mine, setMine] = useState({}); // "d-s": true for THIS student
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [toast, setToast] = useState(null);
  const [day, setDay] = useState(todayIndex()); // phones only
  const toastTimer = useRef(null);

  // ---------- Load (same endpoint) ----------
  useEffect(() => {
    if (!assignedTeacher) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const res = await axios.get(`${API}/api/teacher/paidavailability/${assignedTeacher}`);
        if (cancelled) return;
        const open = {};
        (res.data?.availability || []).forEach((a) => {
          if (a.Checkteacher === 1) open[keyOf(a.DayoftheWeek, a.Slot)] = true;
        });
        const taken = {};
        const own = {};
        (res.data?.students || []).forEach((st) => {
          const k = keyOf(st.DayoftheWeek, st.Slot);
          taken[k] = true;
          // the id in the address is text, the server's is a number: compare as text
          if (String(st.IdUser) === String(studentId)) own[k] = true;
        });
        setTeacherOpen(open);
        setAllTaken(taken);
        setMine(own);
      } catch (err) {
        console.error('Error fetching availability:', err);
        if (!cancelled) setError('We couldn’t load the teacher’s availability.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assignedTeacher, studentId, reload]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const showToast = (message, tone) => {
    clearTimeout(toastTimer.current);
    setToast({ message, tone, id: Date.now() });
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  };

  const stateOf = (d, s) => {
    const k = keyOf(d, s);
    if (mine[k]) return 'mine';
    if (!teacherOpen[k]) return 'closed';
    if (allTaken[k]) return 'taken';
    return 'free';
  };

  // ---------- Toggle (same endpoint + same payload), put back on error ----------
  const toggle = async (d, s) => {
    const k = keyOf(d, s);
    const state = stateOf(d, s);
    if (saving[k] || (state !== 'mine' && state !== 'free')) return;
    const next = state === 'free';

    const apply = (on) => {
      setMine((p) => ({ ...p, [k]: on }));
      setAllTaken((p) => ({ ...p, [k]: on }));
    };
    apply(next);
    setSaving((p) => ({ ...p, [k]: true }));
    try {
      await axios.post(
        `${API}/api/admin/paidavailability/submitstudentpaidavailability`,
        { StudentId: studentId, dayOfWeek: d, slot: s, isAvailable: next },
        { headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } }
      );
      showToast(`${SHORT[d - 1]} ${SLOT_LABEL[s]} ${next ? 'given to this student' : 'removed'}`, next ? 'on' : 'off');
    } catch (err) {
      console.error('Error submitting availability:', err);
      apply(!next);
      showToast('Couldn’t save. Please try again.', 'error');
    } finally {
      setSaving((p) => {
        const c = { ...p };
        delete c[k];
        return c;
      });
    }
  };

  /* ---------- Counts ---------- */
  const mineList = [];
  for (let d = 1; d <= 7; d += 1) SLOTS.forEach((s) => mine[keyOf(d, s)] && mineList.push([d, s]));
  const freeCount = Object.keys(teacherOpen).filter((k) => teacherOpen[k] && !allTaken[k]).length;
  const perDay = (d) => ({
    free: SLOTS.filter((s) => stateOf(d, s) === 'free').length,
    mine: SLOTS.filter((s) => stateOf(d, s) === 'mine').length,
  });

  /* One cell, used in the grid and in the phone list */
  const renderCell = (d, s, variant) => {
    const k = keyOf(d, s);
    const state = stateOf(d, s);
    const time = SLOT_LABEL[s];

    if (state === 'taken' || state === 'closed') {
      const taken = state === 'taken';
      return (
        <div className={`rmasa-cell ${taken ? 'is-booked' : 'is-closed'} rmasa-cell--${variant}`} title={taken ? 'Another student has this slot' : 'Teacher not available'}>
          <FontAwesomeIcon icon={taken ? faUserGraduate : faBan} className="rmasa-cell-icon" />
          <span className="rmasa-cell-text">
            <strong>{taken ? 'Taken' : 'Not available'}</strong>
            {variant === 'grid' && taken && <span>{time}</span>}
          </span>
          {variant === 'row' && <span className="rmasa-row-time">{time}</span>}
        </div>
      );
    }

    const on = state === 'mine';
    return (
      <button
        type="button"
        className={`rmasa-cell ${on ? 'is-on' : 'is-off'} ${saving[k] ? 'is-saving' : ''} rmasa-cell--${variant}`}
        onClick={() => toggle(d, s)}
        aria-pressed={on}
        aria-label={`${DAYS[d - 1]} ${time}: ${on ? 'this student’s slot, tap to remove' : 'free, tap to give to this student'}`}
      >
        {variant === 'row' ? (
          <>
            <span className="rmasa-row-time">{time}</span>
            <span className="rmasa-row-state">{on ? 'Your slot' : 'Available'}</span>
            <span className="rmasa-switch" aria-hidden="true">
              <i />
            </span>
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={on ? faCheck : faPlus} className="rmasa-cell-icon" />
            <span className="rmasa-cell-text">
              <strong>{time}</strong>
              <span>{on ? 'Your slot' : 'Available'}</span>
            </span>
          </>
        )}
      </button>
    );
  };

  if (loading) {
    return (
      <div className="rmasa" aria-busy="true">
        <div className="rmasa-skel rmasa-skel--top" />
        <div className="rmasa-skel rmasa-skel--grid" />
      </div>
    );
  }

  if (!assignedTeacher) {
    return (
      <div className="rmasa">
        <div className="rmasa-empty">
          <FontAwesomeIcon icon={faUserGraduate} />
          <p>Assign a teacher first (Edit & teacher tab) to choose this student’s slots.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rmasa">
        <div className="rmasa-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <p>{error}</p>
          <button type="button" className="rmasa-btn" onClick={() => setReload((n) => n + 1)}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rmasa">
      {/* ---------- Intro + counts ---------- */}
      <div className="rmasa-top">
        <div>
          <h2>Weekly slots</h2>
          <p>
            Tap a free time to give it to this student, tap their slot to remove it. Repeats every week.{' '}
            <span className="rmasa-tz">
              <FontAwesomeIcon icon={faGlobe} /> Lebanon time
            </span>
          </p>
        </div>
        <ul className="rmasa-stats">
          <li className="is-on">
            <strong>{mineList.length}</strong> this student
          </li>
          <li className="is-off">
            <strong>{freeCount}</strong> free
          </li>
        </ul>
      </div>

      {/* this student's slots at a glance */}
      <ul className="rmasa-mine" aria-label="This student’s slots">
        <li className="rmasa-mine-label">This student</li>
        {mineList.length ? (
          mineList.map(([d, s]) => (
            <li key={keyOf(d, s)}>
              <FontAwesomeIcon icon={faCheck} /> {SHORT[d - 1]} {SLOT_LABEL[s]}
            </li>
          ))
        ) : (
          <li className="rmasa-dim">No slots yet</li>
        )}
      </ul>

      <ul className="rmasa-legend">
        <li className="is-on">
          <i /> Your slot
        </li>
        <li className="is-off">
          <i /> Available
        </li>
        <li className="is-booked">
          <i /> Taken
        </li>
        <li className="is-closed">
          <i /> Teacher not available
        </li>
      </ul>

      {/* ---------- Grid (tablet / desktop) ---------- */}
      <div className="rmasa-wrap">
        <div className="rmasa-grid">
          <div className="rmasa-corner" />
          {SHORT.map((label, i) => {
            const c = perDay(i + 1);
            return (
              <div key={label} className={`rmasa-dayhead ${todayIndex() === i + 1 ? 'is-today' : ''}`}>
                <strong>{label}</strong>
                <span>
                  {c.mine ? `${c.mine} mine · ` : ''}
                  {c.free} free
                </span>
              </div>
            );
          })}

          {SLOTS.map((s) => (
            <React.Fragment key={s}>
              <div className="rmasa-time">{SLOT_LABEL[s]}</div>
              {SHORT.map((_, i) => (
                <React.Fragment key={keyOf(i + 1, s)}>{renderCell(i + 1, s, 'grid')}</React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ---------- Day list (phones) ---------- */}
      <div className="rmasa-mobile">
        <div className="rmasa-days" role="tablist" aria-label="Day">
          {SHORT.map((label, i) => {
            const d = i + 1;
            const c = perDay(d);
            return (
              <button
                key={label}
                type="button"
                role="tab"
                aria-selected={day === d}
                className={`${day === d ? 'is-on' : ''} ${todayIndex() === d ? 'is-today' : ''}`}
                onClick={() => setDay(d)}
              >
                <span>{label}</span>
                <strong>{c.mine + c.free}</strong>
              </button>
            );
          })}
        </div>
        <ul className="rmasa-list">
          {SLOTS.map((s) => (
            <li key={s}>{renderCell(day, s, 'row')}</li>
          ))}
        </ul>
      </div>

      {toast && (
        <div key={toast.id} className={`rmasa-toast is-${toast.tone}`} role="status" aria-live="polite">
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default StudentPaidAvailability;